/**
 * Admin-configurable look of the required / soft-required markers on a field label.
 *
 * Stored on `settings/fieldRules` alongside the per-field rules, because this describes what
 * the requirement LEVELS mean firm-wide rather than anything about one field:
 *
 *   indicators: {
 *     required:      { symbol: '*', color: 'red' },
 *     soft_required: { symbol: '◦', color: 'amber' },
 *   }
 *
 * An absent `indicators` key (or an absent settings/fieldRules document entirely) resolves to
 * the historical defaults — a red `*` and an amber `◦` — so this is a true no-op until an
 * admin changes something, per the repo's absent-settings-doc invariant.
 *
 * COLORS ARE AN ALLOWLIST OF FULL CLASS STRINGS, not a colour name interpolated into a
 * template. Tailwind 4 resolves classes by scanning source text at build time, so a
 * constructed name like `text-${color}-600` is never emitted into the stylesheet and silently
 * renders unstyled. Every class below appears here as a literal precisely so the scanner
 * finds it. Adding a colour means adding a row here, not accepting arbitrary input.
 */

import { REQUIREMENT } from './fieldRules.js';

/** [id, admin-facing label, symbol] — `''` renders no marker at all. */
export const INDICATOR_SYMBOLS = [
  ['asterisk', 'Asterisk  *', '*'],
  ['star', 'Star  ✱', '✱'],
  ['bullet', 'Filled dot  ●', '●'],
  ['circle', 'Open dot  ○', '○'],
  ['ring', 'Small ring  ◦', '◦'],
  ['middot', 'Middle dot  ·', '·'],
  ['dagger', 'Dagger  †', '†'],
  ['bang', 'Exclamation  !', '!'],
  ['triangle', 'Triangle  ▲', '▲'],
  ['diamond', 'Diamond  ◆', '◆'],
  ['tilde', 'Tilde  ~', '~'],
  ['none', 'No marker', ''],
];

/**
 * [id, admin-facing label, { text, thin, thick }] — every value a literal class string. See
 * the header note on why these cannot be built by interpolation. `text` colours the label
 * marker; `thin`/`thick` outline the field itself when an outline is switched on.
 */
export const INDICATOR_COLORS = [
  ['red', 'Red', {
    text: 'text-red-600 dark:text-red-400',
    thin: 'ring-1 ring-red-400 dark:ring-red-500',
    thick: 'ring-2 ring-red-500 dark:ring-red-400',
  }],
  ['amber', 'Amber', {
    text: 'text-amber-600 dark:text-amber-400',
    thin: 'ring-1 ring-amber-400 dark:ring-amber-500',
    thick: 'ring-2 ring-amber-500 dark:ring-amber-400',
  }],
  ['orange', 'Orange', {
    text: 'text-orange-600 dark:text-orange-400',
    thin: 'ring-1 ring-orange-400 dark:ring-orange-500',
    thick: 'ring-2 ring-orange-500 dark:ring-orange-400',
  }],
  ['green', 'Green', {
    text: 'text-green-700 dark:text-green-400',
    thin: 'ring-1 ring-green-500 dark:ring-green-500',
    thick: 'ring-2 ring-green-600 dark:ring-green-400',
  }],
  ['blue', 'Blue', {
    text: 'text-blue-600 dark:text-blue-400',
    thin: 'ring-1 ring-blue-400 dark:ring-blue-500',
    thick: 'ring-2 ring-blue-500 dark:ring-blue-400',
  }],
  ['violet', 'Violet', {
    text: 'text-violet-600 dark:text-violet-400',
    thin: 'ring-1 ring-violet-400 dark:ring-violet-500',
    thick: 'ring-2 ring-violet-500 dark:ring-violet-400',
  }],
  ['grey', 'Grey', {
    text: 'text-slate-500 dark:text-slate-400',
    thin: 'ring-1 ring-slate-400 dark:ring-slate-500',
    thick: 'ring-2 ring-slate-500 dark:ring-slate-400',
  }],
  ['firm', 'Firm navy', {
    text: 'text-firm-700 dark:text-firm-100',
    thin: 'ring-1 ring-firm-500 dark:ring-firm-500',
    thick: 'ring-2 ring-firm-700 dark:ring-firm-100',
  }],
];

/** [id, admin-facing label] — the outline drawn around the field itself. */
export const HIGHLIGHT_STYLES = [
  ['none', 'No outline'],
  ['thin', 'Thin outline'],
  ['thick', 'Thick outline'],
];

/**
 * [id, admin-facing label] — WHEN the outline appears. This is the setting that matters most.
 * The original hardcoded red ring used 'blank', which meant every required field on a form
 * someone had just opened was outlined in red before they had typed anything, reading as a
 * page full of errors. 'invalid' waits until validation has actually run and flagged the
 * field, which is why it is the default here.
 */
export const HIGHLIGHT_TRIGGERS = [
  ['invalid', 'Only after a failed submit'],
  ['blank', 'Whenever the field is empty'],
];

export const DEFAULT_INDICATORS = {
  [REQUIREMENT.REQUIRED]: { symbol: '*', color: 'red', highlight: 'none', highlightWhen: 'invalid' },
  [REQUIREMENT.SOFT_REQUIRED]: { symbol: '◦', color: 'amber', highlight: 'none', highlightWhen: 'invalid' },
};

const COLOR_BUNDLES = new Map(INDICATOR_COLORS.map(([id, , bundle]) => [id, bundle]));
const COLOR_CLASSES = new Map(INDICATOR_COLORS.map(([id, , bundle]) => [id, bundle.text]));
const SYMBOL_BY_ID = new Map(INDICATOR_SYMBOLS.map(([id, , symbol]) => [id, symbol]));

/** The symbol id whose character matches `symbol`, for driving a <select>'s value. */
export function symbolIdFor(symbol) {
  const found = INDICATOR_SYMBOLS.find(([, , char]) => char === symbol);
  return found ? found[0] : 'none';
}

export const symbolForId = (id) => SYMBOL_BY_ID.get(id) ?? '';

/**
 * Resolves one requirement level's marker to `{ symbol, className }`.
 *
 * Every unrecognised input falls back to that level's default rather than throwing or
 * rendering unstyled: a colour that is not in the allowlist (a hand-edited Firestore value,
 * or one removed from INDICATOR_COLORS in a later release) must not be able to break the
 * form, and the marker is decoration — the requirement itself is enforced by the rules
 * engine regardless of how, or whether, it is drawn.
 */
export function resolveIndicator(requirement, indicators) {
  const fallback = DEFAULT_INDICATORS[requirement];
  if (!fallback) return null;

  const configured =
    indicators && typeof indicators === 'object' && !Array.isArray(indicators)
      ? indicators[requirement]
      : null;
  const raw = configured && typeof configured === 'object' && !Array.isArray(configured) ? configured : {};

  const symbol = typeof raw.symbol === 'string' ? raw.symbol : fallback.symbol;
  const className = COLOR_CLASSES.get(raw.color) || COLOR_CLASSES.get(fallback.color);

  if (!symbol) return null; // an admin chose "No marker" for this level
  return { symbol, className };
}

/** The stored config for one level, with defaults filled in. */
function levelConfig(requirement, indicators) {
  const fallback = DEFAULT_INDICATORS[requirement];
  if (!fallback) return null;
  const configured =
    indicators && typeof indicators === 'object' && !Array.isArray(indicators)
      ? indicators[requirement]
      : null;
  const raw = configured && typeof configured === 'object' && !Array.isArray(configured) ? configured : {};
  return { ...fallback, ...raw };
}

/**
 * The outline classes for a field, or `''` for no outline.
 *
 * @param {string} requirement  this field's requirement level
 * @param {object} indicators   settings/fieldRules.indicators
 * @param {{ blank: boolean, invalid: boolean }} state
 *   `blank`   — required-and-empty right now (fieldRules.js's isLiveIncomplete)
 *   `invalid` — validation has run and flagged this field
 *
 * Returns `''` unless the level has an outline switched on AND its trigger currently matches.
 * A 'blank' trigger also fires on `invalid`, since a field flagged by validation is a strictly
 * stronger signal than merely being empty — an admin who asked for the noisier option should
 * not somehow see less.
 */
export function resolveHighlight(requirement, indicators, state = {}) {
  const config = levelConfig(requirement, indicators);
  if (!config || config.highlight === 'none') return '';

  const bundle = COLOR_BUNDLES.get(config.color) || COLOR_BUNDLES.get(DEFAULT_INDICATORS[requirement].color);
  const outline = bundle[config.highlight];
  if (!outline) return ''; // an unrecognised style is treated as "no outline", never as a crash

  const active = config.highlightWhen === 'blank' ? Boolean(state.blank) || Boolean(state.invalid) : Boolean(state.invalid);
  return active ? `rounded ${outline}` : '';
}
